export interface RaceRoute {
  roomId: string;
  inviteToken: string;
}

export interface RouteEnvironment extends EventTarget {
  readonly location: {
    readonly pathname: string;
    readonly search: string;
    readonly origin: string;
  };
  readonly history: {
    pushState(data: unknown, unused: string, url?: string | URL | null): void;
    replaceState(data: unknown, unused: string, url?: string | URL | null): void;
  };
}

/** Centralises browser-route parsing, URL construction, and popstate ownership. */
export class RouteController {
  private stopListening: (() => void) | null = null;

  constructor(private readonly environment: RouteEnvironment) {}

  read(): RaceRoute | null {
    const match = this.environment.location.pathname.match(/^\/race\/([a-zA-Z0-9_-]+)\/?$/);
    if (!match?.[1]) return null;
    return {
      roomId: match[1],
      inviteToken: new URLSearchParams(this.environment.location.search).get('invite') ?? '',
    };
  }

  start(onNavigate: (route: RaceRoute | null) => void): void {
    this.stop();
    const listener = (): void => onNavigate(this.read());
    this.environment.addEventListener('popstate', listener);
    this.stopListening = () => this.environment.removeEventListener('popstate', listener);
  }

  stop(): void {
    this.stopListening?.();
    this.stopListening = null;
  }

  raceUrl(roomId: string, inviteToken: string): string {
    const path = `/race/${encodeURIComponent(roomId)}`;
    return inviteToken ? `${path}?invite=${encodeURIComponent(inviteToken)}` : path;
  }

  absoluteRaceUrl(roomId: string, inviteToken: string): string {
    return `${this.environment.location.origin}${this.raceUrl(roomId, inviteToken)}`;
  }

  pushRace(roomId: string, inviteToken: string): void {
    this.push(this.raceUrl(roomId, inviteToken));
  }

  replaceRace(roomId: string, inviteToken: string): void {
    this.replace(this.raceUrl(roomId, inviteToken));
  }

  pushHome(): void {
    this.push('/');
  }

  push(url: string): void {
    this.environment.history.pushState({}, '', url);
  }

  replace(url: string): void {
    this.environment.history.replaceState({}, '', url);
  }
}
